/**
 * Kind-aware UI labels for variant chips and badges (PR-N2).
 *
 * Replaces the legacy single-letter ("Variant A", "Variant B") strings
 * that appeared on RoutingStrip, ColorFilmstrip, DecorationInspector,
 * and VariantMenu. Single letters no longer encode meaningful routing
 * after PR-J's kind enum landed -- a "specific_colors" variant has no
 * natural letter, a catch_all_light variant on placement 1 has nothing
 * to do with the variant_key "A" on placement 2's catch_all_light. The
 * labels here express the ROUTING ROLE (light / dark / all / custom)
 * directly.
 *
 * Public surface (locked):
 *
 *   variantKindLabel(variant, opts?): string
 *     Returns the short human-readable label for a Variant.
 *     - catch_all_light  -> "Light variant"
 *     - catch_all_dark   -> "Dark variant"
 *     - single_catch_all -> "All colors"
 *     - specific_colors  -> "Custom: <name1>, <name2>" (truncated)
 *     - null / unknown   -> ""
 *
 *   variantKindCssTag(variant): string
 *     Returns the suffix portion of the legacy "vtag-X" CSS class.
 *     - catch_all_light  -> "light"
 *     - catch_all_dark   -> "dark"
 *     - single_catch_all -> "single"
 *     - specific_colors  -> "specific"
 *     - null / unknown   -> "none"
 *
 *   variantKindBadgeText(variant): string
 *     Returns the SHORT badge body (filmstrip "vtag-light/dark/..." dots).
 *     - catch_all_light  -> "L"
 *     - catch_all_dark   -> "D"
 *     - single_catch_all -> "*"
 *     - specific_colors  -> "S"
 *     - null / unknown   -> "?"
 *
 * Truncation of specific_colors names: at most 2 names are inlined;
 * additional colors collapse to "+N more". This keeps the chip width
 * stable on a crowded filmstrip without losing the routing signal.
 *
 * The opts.productColors argument (used by ``variantKindLabel`` for
 * specific_colors variants) is an array of ``{color_sku, name}`` rows
 * the caller already has on hand -- typically supplierMeta.colors or
 * the cart's resolved color list. When a SKU is not found in the list,
 * the SKU string itself is shown.
 */

import {
  VARIANT_KIND_CATCH_ALL_DARK,
  VARIANT_KIND_CATCH_ALL_LIGHT,
  VARIANT_KIND_SINGLE_CATCH_ALL,
  VARIANT_KIND_SPECIFIC_COLORS,
} from '@/utils/decorationRouting'

const MAX_INLINE_SPECIFIC_NAMES = 2

function nameForSku(sku, productColors) {
  if (!Array.isArray(productColors)) return sku
  const row = productColors.find((c) => c && c.color_sku === sku)
  return row?.name || sku
}

export function variantKindLabel(variant, opts = {}) {
  const kind = variant?.kind
  if (!kind) return ''
  if (kind === VARIANT_KIND_CATCH_ALL_LIGHT) return 'Light variant'
  if (kind === VARIANT_KIND_CATCH_ALL_DARK) return 'Dark variant'
  if (kind === VARIANT_KIND_SINGLE_CATCH_ALL) return 'All colors'
  if (kind === VARIANT_KIND_SPECIFIC_COLORS) {
    const skus = Array.isArray(variant.color_skus) ? variant.color_skus : []
    if (skus.length === 0) return 'Custom'
    const productColors = opts.productColors ?? []
    const names = skus.map((sku) => nameForSku(sku, productColors))
    if (names.length <= MAX_INLINE_SPECIFIC_NAMES) {
      return `Custom: ${names.join(', ')}`
    }
    const head = names.slice(0, MAX_INLINE_SPECIFIC_NAMES).join(', ')
    const extra = names.length - MAX_INLINE_SPECIFIC_NAMES
    return `Custom: ${head} +${extra} more`
  }
  return ''
}

export function variantKindCssTag(variant) {
  const kind = variant?.kind
  if (kind === VARIANT_KIND_CATCH_ALL_LIGHT) return 'light'
  if (kind === VARIANT_KIND_CATCH_ALL_DARK) return 'dark'
  if (kind === VARIANT_KIND_SINGLE_CATCH_ALL) return 'single'
  if (kind === VARIANT_KIND_SPECIFIC_COLORS) return 'specific'
  return 'none'
}

export function variantKindBadgeText(variant) {
  const kind = variant?.kind
  if (kind === VARIANT_KIND_CATCH_ALL_LIGHT) return 'L'
  if (kind === VARIANT_KIND_CATCH_ALL_DARK) return 'D'
  if (kind === VARIANT_KIND_SINGLE_CATCH_ALL) return '*'
  if (kind === VARIANT_KIND_SPECIFIC_COLORS) return 'S'
  return '?'
}
